export default customElements.define('w-segmented-control', class SegmentedControl extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-segmented-control';
        const config = {
            styleSelector: styleSelector,
            templateHTML: `
            <div class="${styleSelector}__container" data-root>
                <span class="${styleSelector}__slider" id="slider" data-slider></span>
            </div>`,
            cacheDom: ['data-slider'],
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);
        };
    }

    static get requiredItemsProperties () {
        return [
            'label',
            'value'
        ];
    }

    static get validStyles () {
        return [
            'buttons',
            'tabs'
        ];
    }

    static get observedProps() {
        return [
            new wealthify.Prop('items', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.requiredItemsProperties)]
            }),
            new wealthify.Prop('key', { required: true }),
            new wealthify.Prop('selected'),
            new wealthify.Prop('stacked', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('styleprop', {
                default: 'tabs',
                validators: [wealthify.propValidators.IsInArray(this.validStyles)]
            })
        ];
    }

    get propCallbacks() {
        return {
            items: () => {
                this.dom.radiogroups = [];

                while (this.dom.root.lastChild.id !== 'slider') {
                    this.dom.root.removeChild(this.dom.root.lastChild);
                }

                this.items.forEach((item, index) => {
                    this.dom.radiogroups[index] = {};

                    this._createItem(item, index);
                });

                this._setSelectedItem(this.selected ?? 1);

                this._setSliderPosition();
            },

            key: () => {
                if (!this.dom.radiogroups) {
                    return;
                }
        
                Array.prototype.forEach.call(this.dom.radiogroups, (group, index) => {
                    this._addInputAttributes(index);
                });
            },

            selected: () => {
                if (!this.dom.radiogroups) {
                    return;
                }

                this._setSelectedItem(this.selected);
            },

            stacked: () => {
                const classListAction = this.stacked ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}--stacked`);

                this._setSliderPosition(true);
            },

            styleprop: () => {
                this.constructor.validStyles.forEach((type) => {
                    this.classList.remove(`${this.styleSelector}--${type}`);
                    this.dom.slider.classList.remove(`${this.styleSelector}__slider--${type}`);
                    this.dom.slider.classList.remove(`${this.styleSelector}__slider--${type}`);
                });

                this.classList.add(`${this.styleSelector}--${this.styleprop}`);
                this.dom.slider.classList.add(`${this.styleSelector}__slider--${this.styleprop}`);
            }
        };
    }

    _createItem(item, index) {
        const label = this.dom.radiogroups[index].label = document.createElement('label');

        label.classList.add(`${this.styleSelector}__item`);
        label.setAttribute('data-label', '');
        label.innerHTML = item.label;

        const input = this.dom.radiogroups[index].input = document.createElement('input');

        input.classList.add(`${this.styleSelector}__item-input`);
        input.type = 'radio';
        input.value = item.value;

        this._addInputAttributes(index);

        if (this.selected === index) {
            input.checked = true;
            this._selectOption(index);
        }

        input.addEventListener('change', () => {
            this._selectOption(index);
        });

        this.dom.root.appendChild(label);
        this.dom.root.appendChild(input);
    }

    _addInputAttributes (index) {
        if (this.key === undefined) {
            return;
        }

        const inputId = `${this.key}-${index}`;

        this.dom.radiogroups[index].input.name = this.key;
        this.dom.radiogroups[index].input.id = inputId;
        this.dom.radiogroups[index].label.setAttribute('for', inputId);

        if (this.dom.radiogroups[index].description) {
            const descriptionId = `description-${inputId}`;

            this.dom.radiogroups[index].description.id = descriptionId;
            this.dom.radiogroups[index].input.setAttribute('aria-describedby', descriptionId);

            this._updateButtons();
        }
    }

    _getIndexByValue (value) {
        for (var i = 0; i < this.dom.radiogroups.length; i++) {
            if (this.dom.radiogroups[i].input.value == value) {
                return i;
            }
        }

        return 0;
    }

    _selectOption (index) {
        this.value = this.dom.radiogroups[index].input.value;

        this._updateButtons(index);
        this._setSliderPosition();

        const event = document.createEvent('Event');
                        
        event.initEvent('valuechange', true, true);
        this.dispatchEvent(event);
    }

    _updateButtons(index) {
        if (!this.dom.radiogroups) {
            return;
        }

        Array.prototype.forEach.call(this.dom.radiogroups, (group) => {
            group.label.classList.remove(`${this.styleSelector}__item--active`);
            group.input.checked = false;

            this.constructor.validStyles.forEach((type) => {
                group.label.classList.remove(`${this.styleSelector}__item--${type}`);
            });

            group.label.classList.add(`${this.styleSelector}__item--${this.styleprop}`);
        });

        this.dom.radiogroups[index].label.classList.add(`${this.styleSelector}__item--active`);
        this.dom.radiogroups[index].input.checked = true;

        this.valid = this.dom.radiogroups[0].input.checkValidity();
    }

    _setSelectedItem(value) {
        let index = this._getIndexByValue(value);

        this._selectOption(index);
    }

    _setSliderPosition(reset = false) {
        if (!this.dom.radiogroups) {
            return;
        }

        if (reset) {
            this.dom.slider.classList.add('u-transition--none');
            this.dom.slider.style[this.stacked ? 'left' : 'top'] = '0';
        }

        let percent = 100 / this.dom.radiogroups.length;
        
        let currentlySelected = this._getIndexByValue(this.value);

        this.dom.slider.style.height = this.stacked ? `${percent}%` : '100%';
        this.dom.slider.style.width = this.stacked ? '100%' : `${percent}%`;
        this.dom.slider.style[this.stacked ? 'top' : 'left'] = `${percent * currentlySelected}%`;
        this.dom.slider.offsetHeight; // Reflow trigger to prevent animation if u-transition--none class is present

        this.dom.slider.classList.remove('u-transition--none');
    }
});